import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ExternalLink,
  BookOpen,
  CheckCircle2,
  Bot,
  Sparkles,
} from "lucide-react";
import experienceData from "../data/experience.json";

// Register GSAP ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

const Experience = () => {
  const sectionRef2 = useRef(null);
  const lineRef = useRef(null);
  const itemsRef = useRef([]);

  useEffect(() => {
    let ctx = gsap.context(() => {
      // 1. Animate the main gradient line height based on scroll
      // Starts exactly when the section top hits the vertical center of the screen
      gsap.fromTo(
        lineRef.current,
        { height: "0%" },
        {
          height: "100%",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef2.current,
            start: "top -190%",
            end: "bottom 0%",
            scrub: true,
          },
        },
      );

      // 2. Animate each experience block exactly when the line reaches it
      itemsRef.current.forEach((item, i) => {
        if (!item) return;

        const dot = item.querySelector(".timeline-dot");
        const content = item.querySelector(".timeline-content");

        // The Dot "Pop" & Glow (Triggered exactly at 50% viewport, syncing with the line)
        gsap.fromTo(
          dot,
          {
            backgroundColor: "#020617",
            borderColor: "#334155",
            boxShadow: "0 0 0px rgba(168, 85, 247, 0)",
            scale: 1,
          },
          {
            backgroundColor: "#c084fc", // purple-400
            borderColor: "#c084fc",
            boxShadow: "0 0 25px rgba(168, 85, 247, 0.8)",
            scale: 1.4,
            duration: 0.5,
            ease: "back.out(2)", // Adds a bouncy, elastic pop
            scrollTrigger: {
              trigger: item,
              start: "top -80%", // Syncs perfectly with the line's scrub position
              toggleActions: "play none none reverse",
            },
          },
        );

        // Content fade in & slide up (Triggered slightly before dot for smooth reading)
        gsap.fromTo(
          content,
          { opacity: 0, y: 40, filter: "blur(5px)" },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
              trigger: item,
              start: "top -70%",
              toggleActions: "play none none reverse",
            },
          },
        );
      });
    }, sectionRef2);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef2}
      id="experience"
      className="relative min-h-screen text-slate-200 py-24 md:py-32 overflow-hidden border-t border-white/5 font-sans"
    >
      {/* Background Atmosphere */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:32px_32px]"></div>
      <div className="absolute top-1/4 right-0 w-[50vw] h-[50vw] bg-purple-600/10 rounded-full blur-[150px] opacity-40 pointer-events-none"></div>
      <div className="absolute bottom-1/4 left-0 w-[50vw] h-[50vw] bg-blue-600/10 rounded-full blur-[150px] opacity-30 pointer-events-none"></div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20 md:mb-32"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-8 bg-purple-500"></div>
            <span className="text-purple-400 font-medium tracking-widest uppercase text-sm">
              My Journey
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-extrabold text-white">
            Professional{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
              Experience
            </span>
          </h2>
        </motion.div>

        {/* Timeline Container */}
        <div className="relative">
          {/* Default Background Line (Dimmed) */}
          <div className="absolute top-0 bottom-0 left-[20px] md:left-[25%] w-px bg-white/5"></div>

          {/* Animated Gradient Line (Filled by scroll) */}
          <div
            ref={lineRef}
            className="absolute top-0 left-[20px] md:left-[25%] w-px bg-gradient-to-b from-purple-500 via-blue-500 to-transparent"
          ></div>

          {}
          {/* Timeline Items */}
          <div className="flex flex-col gap-16 md:gap-32 pb-24">
            {experienceData.map((item, index) => (
              <div
                key={index}
                ref={(el) => (itemsRef.current[index] = el)}
                className="relative flex flex-col md:flex-row group"
              >
                {/* TIMELINE DOT (GSAP Controlled) */}
                <div className="absolute left-[20px] md:left-[25%] top-1 md:top-2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-slate-700 bg-[#020617] z-20 timeline-dot"></div>

                {item.isCta ? (
                  // --- CTA BLOCK ---
                  <div className="ml-14 md:ml-0 md:w-full flex flex-col items-start md:items-center text-left md:text-center mt-12 timeline-content">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-medium mb-6">
                      <Sparkles className="w-4 h-4" /> Next Chapter
                    </div>
                    <h3 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                      Ready to build <br className="hidden md:block" /> the
                      future?
                    </h3>
                    <p className="text-slate-400 text-lg md:text-xl max-w-xl mx-auto mb-8">
                      I'm currently open for new opportunities. Let's discuss
                      your product and build something meaningful.
                    </p>
                    <button className="px-8 py-4 font-bold text-white rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 hover:scale-105 transition-all shadow-[0_0_20px_rgba(147,51,234,0.3)] flex items-center gap-3">
                      <Bot className="w-5 h-5" /> Start a Conversation
                    </button>
                  </div>
                ) : (
                  // --- STANDARD EXPERIENCE BLOCK ---
                  <>
                    {/* LEFT COL: Year (Desktop) */}
                    <div className="hidden md:block md:w-[25%] pr-12 text-right pt-1 timeline-content">
                      <span className="font-mono text-xl font-bold text-slate-500 block mb-2">
                        {item.year}
                      </span>
                      <span className="text-sm text-slate-500 tracking-wide uppercase font-medium">
                        {item.duration}
                      </span>
                    </div>

                    {/* RIGHT COL: Content */}
                    <div className="ml-14 md:ml-0 md:w-[75%] md:pl-16 timeline-content">
                      {/* Mobile Year & Duration (Hidden on desktop) */}
                      <div className="md:hidden flex items-center gap-3 mb-4">
                        <span className="font-mono text-lg font-bold text-purple-400">
                          {item.year}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-slate-600"></span>
                        <span className="text-xs text-slate-400 uppercase tracking-wide">
                          {item.duration}
                        </span>
                      </div>

                      {/* Header: Role & Company */}
                      <div className="flex flex-col mb-6">
                        <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">
                          {item.role}
                        </h3>
                        <a
                          href={item.link || "#"}
                          target="_blank"
                          className="text-xl text-purple-400 hover:text-purple-300 transition-colors font-medium flex items-center gap-2 w-max group/link"
                        >
                          {item.logo && (
                            <img
                              src={`/assets/experience/${item.image}`}
                              alt={item.company}
                              className="w-9 h-9 rounded-full object-cover"
                            />
                          )}
                          {item.company}
                          <ExternalLink className="w-4 h-4 opacity-0 -translate-x-2 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all" />
                        </a>
                      </div>

                      {/* Description */}
                      <p className="text-slate-300 text-lg leading-relaxed mb-8">
                        {item.description}
                      </p>

                      {/* Info Grid (Icons, Skills, Learnings) */}
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-10">
                        {/* Stack & Skills */}
                        <div className="space-y-8">
                          <div>
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">
                              Tech Stack
                            </h4>
                            <div className="flex flex-wrap gap-3">
                              {item.technologies?.map((tech, i) => (
                                <div
                                  key={i}
                                  className="group/tech relative w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors cursor-help"
                                >
                                  <span className="text-lg">{tech.icon}</span>
                                  <span className="absolute -top-10 scale-0 opacity-0 group-hover/tech:scale-100 group-hover/tech:opacity-100 transition-all bg-black border border-white/10 text-white text-xs px-3 py-1.5 rounded-md whitespace-nowrap z-10 pointer-events-none">
                                    {tech.name}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">
                              Core Skills
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {item.skills?.map((skill, i) => (
                                <span
                                  key={i}
                                  className="px-3 py-1 text-xs font-medium text-slate-300 bg-white/5 border border-white/10 rounded-full"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {}
                        {/* Learnings & Achievements */}
                        <div className="space-y-8">
                          {item.learnings?.length > 0 && (
                            <div>
                              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                                <BookOpen className="w-4 h-4" /> Key Learnings
                              </h4>
                              <ul className="space-y-2">
                                {item.learnings.map((learn, i) => (
                                  <li
                                    key={i}
                                    className="text-sm text-slate-400 flex items-start gap-2"
                                  >
                                    <span className="text-purple-500/70 mt-0.5">
                                      💡
                                    </span>{" "}
                                    {learn}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {item.achievements?.length > 0 && (
                            <div>
                              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />{" "}
                                Achievements
                              </h4>
                              <ul className="space-y-2">
                                {item.achievements.map((achieve, i) => (
                                  <li
                                    key={i}
                                    className="text-sm text-emerald-400/90 font-medium"
                                  >
                                    {achieve}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Optional Trust Docs (Certificates) */}
                      {item.docs && item.docs.length > 0 && (
                        <div className="mt-10 pt-6 border-t border-white/5 flex flex-wrap gap-4">
                          {item.docs.map((doc, i) => (
                            <a
                              href={doc.link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <button
                                key={i}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-300 hover:bg-white/10 hover:text-white transition-all"
                              >
                                <span>{doc.icon}</span> {doc.title}
                              </button>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
